/**
 * useGridData — turn raw bookings + AppData reference tables into the
 * structured rows the grid renders.
 *
 * Responsibilities:
 *  - compute synthetic AutoBlock rows when a linked-group neighbour is booked
 *  - detect conflict-leads (booking + lead on the same slot)
 *  - filter quests by selected branch
 *  - compute shifted slot times for "shifting" branches when a tea-room
 *    occupies the gap before/after a booked slot
 *
 * Copied 1:1 from v3 hooks/useGridData.ts. Хардкод BRANCH_CONFIG развязан:
 * конфиг филиала теперь приходит из БД через AppData.branches (./branchConfig).
 */

import { useMemo } from "react";
import { baseSlotOf, mcMinutes, nextSlotAfter, slotsBlockedBy } from "@ub/shared";
import type { Branch, LinkedGroup, MasterClass, Quest, QuestVersion, Slot } from "../api/types";
import type { AutoBlock, Booking, BookingsMap, ConflictMap } from "./bookingTypes";
import { getBranchConfig } from "./branchConfig";
import { slotSort } from "../utils/date";
import { computeShiftedSlots, keepInLeftColumn, occupancyOf, type ShiftedSlot } from "./shiftedSlots";

/** Длительность спецверсии для автоблоков (строка /api/quest-versions; NULL-поле — база квеста). */
export type VersionSpan = Pick<QuestVersion, "id" | "duration_min" | "reset_min">;

/* 26.09 (владелец: «бронь дурки не перекрывает фазмофобию, хотя это связанные квесты»).
   Автоблок ставился только на ТОЧНО то же время: Дурка на нестандартные 12:30 не гасила
   у Фазмофобии ничего — слота 12:30 у неё нет, — хотя её 13:00 целиком внутри игры
   Дурки. Гейт на сервере при этом отказывал («одна локация»), и сотрудники 25 раз с
   01.08 продавливали бронь, веря сетке. Теперь гасим по ПЕРЕСЕЧЕНИЮ интервалов той же
   формулой, что гейт (slotsBlockedBy, @ub/shared): у связанных — только игры (27.09: уборка
   готовит свою комнату, соседней не мешает), у своего квеста — игра + уборка. Бронь спецверсии
   держит игру и уборку ВЕРСИИ (27.09, как eff() гейта); пустой слот — кандидат по базе квеста.
   МК сюда НЕ прибавляем — так же, как гейт: он считает МК частью игры только в филиале
   со сдвигом мастер-классом (conflicts.ts: mk = mcOn ? … : 0). Где сдвиг выключен (20б,
   СВО) — МК на занятость не влияет вовсе; где включён — хвост МК уже выражен сдвигом
   следующего слота (computeMcNext), и автоблок поверх сдвинутой ячейки сломал бы её:
   показывала бы 15:45, а открывала 15:00. Первая версия этой правки прибавляла МК
   всегда и гасила на 20б слоты, которые гейт разрешает, — исправлено в тот же день. */
export function computeAutoBlocks(
  bookings: BookingsMap,
  linkedGroups: LinkedGroup[],
  quests: Quest[] = [],
  slots: Slot[] = [],
  versions: VersionSpan[] = [],
): Record<string, AutoBlock> {
  const ab: Record<string, AutoBlock> = {};
  const span = new Map(quests.map((q) => [q.id, (Number(q.duration) || 0) + (Number(q.reset_time) || 0)]));
  /* 27.09 (владелец, «Мир Квестов»: «на 15:00 слот можно открыть»): связанные квесты — только
     игры, уборка готовит свою комнату и соседней не мешает; свой квест — игра + уборка. Как гейт. */
  const game = new Map(quests.map((q) => [q.id, Number(q.duration) || 0]));
  const reset = new Map(quests.map((q) => [q.id, Number(q.reset_time) || 0]));
  const sv = new Map(versions.map((v) => [v.id, v]));
  const qSlots = new Map<string, string[]>();
  for (const s of slots) {
    if (!s.active) continue;
    const arr = qSlots.get(s.quest_id) ?? [];
    arr.push(s.time);
    qSlots.set(s.quest_id, arr);
  }
  for (const b of Object.values(bookings)) {
    /* 27.09 (владелец: «блок закрывает всю площадку»): блок соседа гасит связанные слоты, как игра. */
    if (b.type !== "booking" && b.type !== "lead" && b.type !== "block") continue;
    const booking = b as Booking;
    const start = booking.customTime || booking.time;
    /* 27.09: спецверсия — игра и уборка версии (NULL-поле или неизвестная версия — база), как eff() гейта:
       версия длиннее базы иначе оставляла бы соседу «свободную» ячейку, которую гейт отбивает. */
    const v = booking.specialVersionId ? sv.get(booking.specialVersionId) : undefined;
    const bGame = Number(v?.duration_min ?? game.get(booking.questId) ?? 0);
    const bSpan = bGame + Number(v?.reset_min ?? reset.get(booking.questId) ?? 0);
    /* 27.09 (владелец, «глюк в сетке психушки»): игра в нестандартное время занимает и СВОИ
       соседние слоты — Фазм 21:30 (75 + 45 уборки) держит комнату до 23:30, а 23:00 горел
       свободным, хотя Дурка и Госпиталь рядом уже серые и гейт такую запись отклоняет
       («Окно сброса», conflicts.ts — тот же квест). Как в гейте: только бронь, лид не держит.
       При штатном расписании ничего не меняется: слоты не теснее игры с уборкой. */
    if (booking.type === "booking") {
      for (const slot of slotsBlockedBy(start, bSpan, qSlots.get(booking.questId) ?? [], span.get(booking.questId) ?? 0)) {
        const key = `${booking.questId}_${slot}`;
        if (slot !== start && !bookings[key] && !ab[key]) {
          ab[key] = { questId: booking.questId, time: slot, type: "auto-block", src: booking.questId, srcTime: start };
        }
      }
    }
    for (const g of linkedGroups) {
      const gm = g.members ?? [];
      if (!gm.includes(booking.questId)) continue;
      const linked = gm.filter((m) => m !== booking.questId);
      for (const lid of linked) {
        for (const slot of slotsBlockedBy(start, bGame, qSlots.get(lid) ?? [], game.get(lid) ?? 0)) {
          const key = `${lid}_${slot}`;
          if (!bookings[key] && !ab[key]) {
            ab[key] = { questId: lid, time: slot, type: "auto-block", src: booking.questId, srcTime: start };
          }
        }
      }
    }
  }
  return ab;
}

/**
 * 17.09 (владелец): мастер-класс сдвигает следующий слот — настройка филиала mc_shift
 * (как сдвиг чайной, только в конфигураторе). Для строки квеста: базовый слот → минуты
 * МК игры перед ним (своей или связанного квеста — одна локация). Сдвигается только
 * СЛЕДУЮЩИЙ базовый слот после игры с МК, каскада нет: Амонг 14:00 + Слаймы 45 →
 * 17:00 живёт как 17:45, 20:00 остаётся. Так же считают гейт API и публичные слоты.
 */
function computeMcNext(
  questId: string,
  qSlots: string[],
  bookings: BookingsMap,
  linkedGroups: LinkedGroup[],
  durations: Map<string, number>,
): Map<string, number> {
  const out = new Map<string, number>();
  const family = new Set<string>([questId]);
  for (const g of linkedGroups) {
    const gm = g.members ?? [];
    if (gm.includes(questId)) for (const m of gm) family.add(m);
  }
  for (const b of Object.values(bookings)) {
    if (b.type !== "booking" && b.type !== "lead") continue;
    const bk = b as Booking;
    if (!family.has(bk.questId)) continue;
    const mk = mcMinutes(bk.mc, durations);
    if (mk <= 0) continue;
    const next = nextSlotAfter(qSlots, bk.customTime || bk.time);
    if (next) out.set(next, Math.max(out.get(next) ?? 0, mk));
  }
  return out;
}

function computeConflictLeads(bookings: BookingsMap): ConflictMap {
  const conflicts: ConflictMap = {};
  const bySlot: Record<string, Booking[]> = {};

  for (const b of Object.values(bookings)) {
    if (b.type !== "booking" && b.type !== "lead") continue;
    const key = `${b.questId}_${b.time}`;
    if (!bySlot[key]) bySlot[key] = [];
    bySlot[key].push(b as Booking);
  }

  for (const [, items] of Object.entries(bySlot)) {
    if (items.length < 2) continue;
    const lead = items.find((i) => i.type === "lead");
    if (lead) conflicts[`${lead.questId}_${lead.time}`] = lead;
  }
  return conflicts;
}

function getQuestSlots(questId: string, allSlots: Slot[]): string[] {
  return allSlots
    .filter((s) => s.quest_id === questId && s.active)
    .map((s) => s.time)
    .sort(slotSort);
}

function hm(t: string): number {
  const [h, m] = t.split(":").map(Number);
  return (h ?? 0) * 60 + (m ?? 0);
}

/**
 * Bookings made at a non-standard time (custom_time, e.g. 10:10) have a `time`
 * that matches no slot column, so they vanish from the grid. Re-key each such
 * booking onto its nearest FREE standard slot (auto-blocks may be overwritten).
 * The booking keeps its real time — the Slot shows customTime over the column.
 */
export function remapCustomBookings(merged: BookingsMap, slots: Slot[], preferBase: (questId: string, t: string, col: string) => boolean = () => false): BookingsMap {
  const byQuest = new Map<string, string[]>();
  for (const s of slots) {
    if (!s.active) continue;
    const arr = byQuest.get(s.quest_id) ?? [];
    arr.push(s.time);
    byQuest.set(s.quest_id, arr);
  }
  const out: BookingsMap = { ...merged };
  /* Дистанция по суточному КОЛЬЦУ (21.08, Саша): ночная 00:30 ближе к 23:00
     (90м через полночь), чем к 11:00 — иначе ночной хвост смены прыгал
     в левый край строки. */
  const dist = (a: number, z: number) => { const d = Math.abs(a - z); return Math.min(d, 1440 - d); };
  /* Порядок в строке (10.09, владелец: «не по порядку стоят таймслоты»): сутки считаем
     от открытия, чтобы ночные слоты шли последними, а не первыми. */
  const dayStart = 6 * 60;
  const ord = (t: string) => { const v = hm(t); return v < dayStart ? v + 1440 : v; };

  /* Пересаживаем нестандартные игры ПО ВОЗРАСТАНИЮ их настоящего времени. Колонка годится,
     только если слева от неё стоят игры не позже, а справа — не раньше: иначе в строке
     появляется обгон вроде «19:00, затем 18:00» (10.09, владелец). */
  const customs = Object.entries(merged)
    .filter(([, it]) => it.type === "booking" || it.type === "lead")
    .map(([key, it]) => ({ key, b: it as Booking }))
    .filter(({ b }) => !(byQuest.get(b.questId) ?? []).includes(b.time))
    .sort((a, z) => ord(z.b.time) - ord(a.b.time));   // от поздних к ранним

  /** Время, которое колонка показывает: у занятой — время игры, иначе сама колонка. */
  const shownAt = (questId: string, col: string) => {
    const occ = out[`${questId}_${col}`];
    if (!occ || occ.type === "auto-block") return null;
    const t = (occ as Booking).customTime || (occ as Booking).time || col;
    return ord(t);
  };

  for (const { key, b } of customs) {
    const qSlots = (byQuest.get(b.questId) ?? []).slice().sort((x, y) => ord(x) - ord(y));
    const mine = ord(b.time);
    const fits = qSlots.filter((col, i) => {
      if (shownAt(b.questId, col) !== null) return false;              // колонка занята
      for (let j = 0; j < i; j++) { const v = shownAt(b.questId, qSlots[j]!); if (v !== null && v > mine) return false; }
      for (let j = i + 1; j < qSlots.length; j++) { const v = shownAt(b.questId, qSlots[j]!); if (v !== null && v < mine) return false; }
      return true;
    });
    /* если подходящей нет — берём любую свободную, лишь бы игра не пропала из сетки */
    const pool = fits.length ? fits : qSlots.filter((col) => shownAt(b.questId, col) === null);
    if (!pool.length) continue;
    /* Ближайшая по времени из подходящих. Порядок держит сам отбор: колонка годится, только
       если слева от неё игры не позже, а справа не раньше. Брать самую правую нельзя —
       одиночная игра уезжала в конец строки (ФНАФ 17:00 оказывался после девяти вечера). */
    /* 17.09: у филиала с mc_shift сдвинутая игра садится в колонку, как её считает гейт и публичные
       слоты (последний слот ≤ времени: 18:45 → 17:00), а не в ближайшую (20:00). */
    const col = baseSlotOf(qSlots, b.time), base = col && preferBase(b.questId, b.time, col) ? col : null;
    /* 27.09: при равной дистанции — ПРАВАЯ колонка. Игры идут от поздних к ранним, и ранним
       нужно место слева: Судн 20:00 между 19:00 и 21:00 садился в 19:00, и 18:30 оставалось
       только 21:00 — в строке выходило «20:00, 18:30». */
    const pick = base && pool.includes(base)
      ? base
      : pool.reduce((best, col) => (dist(hm(col), hm(b.time)) <= dist(hm(best), hm(b.time)) ? col : best), pool[0]!);
    out[`${b.questId}_${pick}`] = b;
    delete out[key];
  }
  return out;
}

export interface GridQuestRow {
  questId: string;
  questName: string;
  shortName: string;
  branchId: string;
  duration: number;
  resetTime: number;
  operators: number;
  slots: ShiftedSlot[];
}

export interface GridData {
  merged: BookingsMap;
  conflictLeads: ConflictMap;
  questRows: GridQuestRow[];
}

export function useGridData(
  bookings: BookingsMap,
  quests: Quest[],
  slots: Slot[],
  linkedGroups: LinkedGroup[],
  branches: Branch[],
  branch: string,
  masterClasses: MasterClass[] = [],
  versions: VersionSpan[] = [],
): GridData {
  return useMemo(() => {
    const mcDurations = new Map(masterClasses.map((m) => [String(m.id), Number(m.duration ?? 0)]));
    const autoBlocks = computeAutoBlocks(bookings, linkedGroups, quests, slots, versions);
    /* 04.10: и филиал со сдвигом чайной — игра на месте, сдвинутом чайной (21:15, не дальше 45 минут от колонки), садится в свою колонку 20:30, а не в правую 22:00 */
    const cfgOf = new Map(quests.map((q) => [q.id, getBranchConfig(branches, q.branch_id)]));
    const merged: BookingsMap = remapCustomBookings({ ...autoBlocks, ...bookings }, slots, (qid, t, col) => keepInLeftColumn(cfgOf.get(qid), t, col));
    const conflictLeads = computeConflictLeads(bookings);

    /* Квесты филиала, которого нет в branches (GET отдаёт только active=1),
       не рисуем даже на «ВСЕ»: их getBranchConfig вернул бы {} — shifting и
       правила чайных молча выключены. Деактивация филиала прячет его целиком. */
    const branchIds = new Set(branches.map((b) => b.id));
    const filteredQuests = quests.filter((q) =>
      q.active && branchIds.has(q.branch_id) && (branch === "all" || q.branch_id === branch));

    const questRows: GridQuestRow[] = filteredQuests
      .slice()
      .sort((a, b) => {
        /* Group by branch first, then by quest sort_order inside the branch.
           Без этого ALL-вид смешивает квесты разных филиалов в кашу. */
        const branchCmp = a.branch_id.localeCompare(b.branch_id);
        if (branchCmp !== 0) return branchCmp;
        return (a.sort_order ?? 0) - (b.sort_order ?? 0);
      })
      .map((q) => {
        const qSlots = getQuestSlots(q.id, slots);
        const cfg = getBranchConfig(branches, q.branch_id);
        /* 17.09: сдвиг следующего слота мастер-классом — только у филиала с mc_shift */
        const mcNext = cfg.mcShift ? computeMcNext(q.id, qSlots, bookings, linkedGroups, mcDurations) : new Map<string, number>();
        /* 04.10: лишняя чайная сдвигает строку вперёд цепочкой — занятость брони как у гейта (shiftedSlots.ts) */
        const occ = occupancyOf(q, versions, cfg.mcShift ? (b) => mcMinutes(b.mc, mcDurations) : undefined);
        const shifted = computeShiftedSlots(q.id, qSlots, merged, cfg, mcNext, (Number(q.duration) || 0) + (Number(q.reset_time) || 0), occ);
        return {
          questId: q.id,
          questName: q.name,
          shortName: q.short_name || q.name.slice(0, 4),
          branchId: q.branch_id,
          duration: q.duration,
          resetTime: q.reset_time,
          operators: q.operators,
          slots: shifted,
        };
      });

    return { merged, conflictLeads, questRows };
  }, [bookings, quests, slots, linkedGroups, branches, branch, masterClasses, versions]);
}
